import React, { useState, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { addMonths, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Calculator } from "lucide-react";

const tiposFinanciamento = [
  { value: "custeio", label: "Custeio Agrícola" },
  { value: "investimento", label: "Investimento" },
  { value: "comercializacao", label: "Comercialização" },
  { value: "pronaf", label: "PRONAF" },
  { value: "pronamp", label: "PRONAMP" },
  { value: "fcn", label: "FCO/FNE/FNO" },
  { value: "emprestimo_pessoal", label: "Empréstimo Pessoal" },
  { value: "credito_rural", label: "Crédito Rural" },
  { value: "outro", label: "Outro" }
];

const periodicidades = [
  { value: "mensal", label: "Mensal", meses: 1 },
  { value: "bimestral", label: "Bimestral", meses: 2 },
  { value: "trimestral", label: "Trimestral", meses: 3 },
  { value: "semestral", label: "Semestral", meses: 6 },
  { value: "anual", label: "Anual", meses: 12 },
  { value: "safra", label: "Por Safra", meses: 6 }
];

export default function FinanciamentoForm({ financiamento, safras = [], onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");

  const [formData, setFormData] = useState({
    tipo: "custeio",
    instituicao: "",
    numero_contrato: "",
    valor_total: "",
    valor_liberado: "",
    saldo_devedor: "",
    taxa_juros: "",
    data_contratacao: "",
    data_vencimento: "",
    data_primeiro_vencimento: "",
    total_parcelas: "",
    valor_parcela: "",
    periodicidade: "mensal",
    finalidade: "",
    safra_id: "",
    garantias: "",
    status: "ativo",
    observacoes: ""
  });
  const [saving, setSaving] = useState(false);
  const [gerarParcelas, setGerarParcelas] = useState(true);

  useEffect(() => {
    if (financiamento) {
      setFormData({
        tipo: financiamento.tipo || "custeio",
        instituicao: financiamento.instituicao || "",
        numero_contrato: financiamento.numero_contrato || "",
        valor_total: financiamento.valor_total?.toString() || "",
        valor_liberado: financiamento.valor_liberado?.toString() || "",
        saldo_devedor: financiamento.saldo_devedor?.toString() || "",
        taxa_juros: financiamento.taxa_juros?.toString() || "",
        data_contratacao: financiamento.data_contratacao || "",
        data_vencimento: financiamento.data_vencimento || "",
        data_primeiro_vencimento: financiamento.data_primeiro_vencimento || "",
        total_parcelas: financiamento.total_parcelas?.toString() || "",
        valor_parcela: financiamento.valor_parcela?.toString() || "",
        periodicidade: financiamento.periodicidade || "mensal",
        finalidade: financiamento.finalidade || "",
        safra_id: financiamento.safra_id || "",
        garantias: financiamento.garantias || "",
        status: financiamento.status || "ativo",
        observacoes: financiamento.observacoes || ""
      });
      setGerarParcelas(false);
    }
  }, [financiamento]);

  const calcularParcela = () => {
    const valor = parseFloat(formData.valor_total) || 0;
    const parcelas = parseInt(formData.total_parcelas) || 1;
    const jurosAnual = parseFloat(formData.taxa_juros) || 0;

    if (valor && parcelas) {
      // Cálculo simplificado (Price)
      const jurosTotal = valor * (jurosAnual / 100) * (parcelas / 12);
      const valorTotal = valor + jurosTotal;
      const valorParcela = valorTotal / parcelas;

      setFormData(prev => ({
        ...prev,
        valor_parcela: valorParcela.toFixed(2),
        saldo_devedor: valor.toString()
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = {
      ...formData,
      propriedade_id: propriedadeId,
      safra_id: formData.safra_id || null,
      data_contratacao: formData.data_contratacao || null,
      data_vencimento: formData.data_vencimento || null,
      data_primeiro_vencimento: formData.data_primeiro_vencimento || null,
      valor_total: parseFloat(formData.valor_total) || 0,
      valor_liberado: parseFloat(formData.valor_liberado) || 0,
      saldo_devedor: parseFloat(formData.saldo_devedor) || parseFloat(formData.valor_total) || 0,
      taxa_juros: parseFloat(formData.taxa_juros) || 0,
      total_parcelas: parseInt(formData.total_parcelas) || 0,
      valor_parcela: parseFloat(formData.valor_parcela) || 0,
      parcelas_pagas: financiamento?.parcelas_pagas || 0
    };

    let financiamentoId;

    if (financiamento) {
      await agrogestao.entities.Financiamento.update(financiamento.id, data);
      financiamentoId = financiamento.id;
    } else {
      const created = await agrogestao.entities.Financiamento.create(data);
      financiamentoId = created.id;

      // Gerar parcelas automaticamente
      if (gerarParcelas && data.total_parcelas > 0 && formData.data_primeiro_vencimento) {
        const periodo = periodicidades.find(p => p.value === formData.periodicidade);
        const mesesIntervalo = periodo?.meses || 1;
        const parcelasData = [];

        for (let i = 0; i < data.total_parcelas; i++) {
          const dataVenc = addMonths(new Date(formData.data_primeiro_vencimento), i * mesesIntervalo);
          parcelasData.push({
            propriedade_id: propriedadeId,
            financiamento_id: financiamentoId,
            numero_parcela: i + 1,
            valor_principal: data.valor_total / data.total_parcelas,
            valor_juros: (data.valor_parcela - (data.valor_total / data.total_parcelas)),
            valor_total: data.valor_parcela,
            data_vencimento: format(dataVenc, 'yyyy-MM-dd'),
            status: 'pendente'
          });
        }

        const parcelasCriadas = await Promise.all(
          parcelasData.map(p => agrogestao.entities.ParcelaFinanciamento.create(p))
        );

        if (Array.isArray(parcelasCriadas) && parcelasCriadas.length > 0) {
          const contas = parcelasCriadas.map(p => ({
            propriedade_id: propriedadeId,
            descricao: `Financiamento ${formData.numero_contrato || formData.instituicao || ''} (${p.numero_parcela}/${data.total_parcelas})`,
            valor: p.valor_total || 0,
            data_vencimento: p.data_vencimento,
            categoria: 'financiamento',
            fornecedor: formData.instituicao || '',
            status: 'pendente',
            parcela_atual: p.numero_parcela,
            total_parcelas: data.total_parcelas,
            observacoes: `parcela_financiamento_id:${p.id}`
          }));

          await Promise.all(
            contas.map(c => agrogestao.entities.ContaPagar.create(c))
          );
        }
      }
    }

    setSaving(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Tipo e Instituição */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Financiamento *</Label>
          <Select
            value={formData.tipo}
            onValueChange={(v) => setFormData({ ...formData, tipo: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tiposFinanciamento.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Instituição Financeira *</Label>
          <Input
            placeholder="Ex: Banco do Brasil, Sicredi..."
            value={formData.instituicao}
            onChange={(e) => setFormData({ ...formData, instituicao: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Contrato e Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Número do Contrato</Label>
          <Input
            placeholder="Número do contrato"
            value={formData.numero_contrato}
            onChange={(e) => setFormData({ ...formData, numero_contrato: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={formData.status}
            onValueChange={(v) => setFormData({ ...formData, status: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="em_analise">Em Análise</SelectItem>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="quitado">Quitado</SelectItem>
              <SelectItem value="renegociado">Renegociado</SelectItem>
              <SelectItem value="inadimplente">Inadimplente</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Valores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Valor Total (R$) *</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.valor_total}
            onChange={(e) => setFormData({ ...formData, valor_total: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Valor Liberado (R$)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.valor_liberado}
            onChange={(e) => setFormData({ ...formData, valor_liberado: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Taxa de Juros (% a.a.)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="0,00"
            value={formData.taxa_juros}
            onChange={(e) => setFormData({ ...formData, taxa_juros: e.target.value })}
          />
        </div>
      </div>

      {/* Datas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Data Contratação *</Label>
          <Input
            type="date"
            value={formData.data_contratacao}
            onChange={(e) => setFormData({ ...formData, data_contratacao: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Primeiro Vencimento</Label>
          <Input
            type="date"
            value={formData.data_primeiro_vencimento}
            onChange={(e) => setFormData({ ...formData, data_primeiro_vencimento: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Vencimento Final</Label>
          <Input
            type="date"
            value={formData.data_vencimento}
            onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
          />
        </div>
      </div>

      {/* Parcelas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Total de Parcelas</Label>
          <Input
            type="number"
            placeholder="12"
            value={formData.total_parcelas}
            onChange={(e) => setFormData({ ...formData, total_parcelas: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Periodicidade</Label>
          <Select
            value={formData.periodicidade}
            onValueChange={(v) => setFormData({ ...formData, periodicidade: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodicidades.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Valor da Parcela (R$)</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.01"
              placeholder="0,00"
              value={formData.valor_parcela}
              onChange={(e) => setFormData({ ...formData, valor_parcela: e.target.value })}
            />
            <Button type="button" variant="outline" size="icon" onClick={calcularParcela} title="Calcular">
              <Calculator className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Finalidade e Safra */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Finalidade</Label>
          <Input
            placeholder="Ex: Compra de trator, Custeio safra 24/25..."
            value={formData.finalidade}
            onChange={(e) => setFormData({ ...formData, finalidade: e.target.value })}
          />
        </div>
        {safras.length > 0 && (
          <div className="space-y-2">
            <Label>Safra Vinculada</Label>
            <Select
              value={formData.safra_id}
              onValueChange={(v) => setFormData({ ...formData, safra_id: v === "none" ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma safra" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma</SelectItem>
                {safras.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Garantias */}
      <div className="space-y-2">
        <Label>Garantias</Label>
        <Textarea
          placeholder="Descreva as garantias oferecidas..."
          value={formData.garantias}
          onChange={(e) => setFormData({ ...formData, garantias: e.target.value })}
          rows={2}
        />
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Observações adicionais..."
          value={formData.observacoes}
          onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
          rows={2}
        />
      </div>

      {/* Gerar Parcelas */}
      {!financiamento && (
        <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg">
          <input
            type="checkbox"
            id="gerarParcelas"
            checked={gerarParcelas}
            onChange={(e) => setGerarParcelas(e.target.checked)}
            className="w-4 h-4"
          />
          <label htmlFor="gerarParcelas" className="text-sm text-indigo-700">
            Gerar parcelas automaticamente
          </label>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              {financiamento ? "Atualizar" : "Cadastrar"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
